/*
 * Session paths — the Home page's "pick a lane" layer over the knowledge hub.
 * AREA paths mirror hub roots (plus featured sub-areas); ROLE paths bundle
 * several roots. Everything derives live from /hub/tree, so user-added topics
 * become cards automatically; curated entries only add blurbs/accents.
 */
import APIService from '../../services/APIService';

export const CURATED_AREAS = {
  ai: { blurb: 'RAG, LLMs, transformers, agents, prompting — the modern AI stack.', accent: '#8b5cf6' },
  'ai.nlp': { blurb: 'Classical NLP — embeddings, TF-IDF, n-grams — the foundations under the hype.', accent: '#f472b6' },
  'ai.rag': { blurb: 'Retrieval-augmented generation, end to end: retrievers, rerankers, production.', accent: '#2dd4bf' },
  'ai.llm': { blurb: 'Large language models — training, inference, serving, evaluation.', accent: '#c084fc' },
  ml: { blurb: 'Machine learning core — supervised, unsupervised, evaluation, monitoring.', accent: '#38bdf8' },
  dl: { blurb: 'Deep learning — losses, optimizers, CNNs, sequence models, training dynamics.', accent: '#fbbf24' },
  dsa: { blurb: 'Data structures & algorithms — 9,000+ problems in a real coding workspace.', accent: '#818cf8' },
  system_design: { blurb: 'Scalability, data systems, and the interviews that test them.', accent: '#34d399' },
};

export const ROLE_PATHS = [
  { id: 'role.ml_engineer', kind: 'role', title: 'ML Engineer', accent: '#38bdf8',
    blurb: 'Machine learning + deep learning, foundations to nuance.', rootIds: ['ml', 'dl'] },
  { id: 'role.genai_engineer', kind: 'role', title: 'GenAI Engineer', accent: '#c084fc',
    blurb: 'RAG, LLMs, agents and prompting — the applied GenAI loop.', rootIds: ['ai.rag', 'ai.llm', 'ai.ag', 'ai.pr'] },
  { id: 'role.interview_allrounder', kind: 'role', title: 'Interview All-Rounder', accent: '#fb923c',
    blurb: 'DSA + system design + ML core: the full technical-interview spread.', rootIds: ['dsa', 'system_design', 'ml'] },
];

const FEATURED_SUBAREAS = ['ai.nlp', 'ai.rag', 'ai.llm'];

// ---------------------------------------------------------------------------

export function indexTree(tree) {
  const byId = {};
  const walk = (nodes) => (nodes || []).forEach((n) => { byId[n.id] = n; walk(n.children); });
  walk(tree || []);
  return byId;
}

/** Total questions under a node: DSA carries a live meta.count; hub item_count
 *  is direct-only, so roll children up. */
export function rollupCount(node) {
  if (!node) return 0;
  if (node.meta && node.meta.count) return node.meta.count;
  let n = node.item_count || 0;
  (node.children || []).forEach((c) => { n += rollupCount(c); });
  return n;
}

/** Leaf hub nodes under a node — Session Settings' draft `sel` needs LEAF ids. */
export function leavesOf(node) {
  if (!node) return [];
  if (!node.children || node.children.length === 0) return [node];
  return node.children.flatMap(leavesOf);
}

export const inPath = (cid, rootIds) =>
  (rootIds || []).some((r) => cid === r || String(cid).startsWith(`${r}.`));

/** The current saved session "is" this path when every subject sits inside it. */
export const sessionWithinPath = (session, path) =>
  !!(session && session.subjects && session.subjects.length
     && session.subjects.every((s) => inPath(s.id, path.rootIds)));

/** Area cards (hub roots + featured sub-areas) + role bundles, hub-resolved. */
export function buildPaths(hubTree) {
  const byId = indexTree(hubTree);
  const areas = [];
  (hubTree || []).forEach((root) => {
    const cur = CURATED_AREAS[root.id] || {};
    areas.push({
      id: root.id, kind: 'area', title: root.name, rootIds: [root.id],
      blurb: cur.blurb || `Your topic — ${root.name}. Every mode practices it.`,
      accent: cur.accent || '#818cf8',
      count: rollupCount(root),
    });
  });
  FEATURED_SUBAREAS.forEach((id) => {
    const node = byId[id];
    if (!node) return;
    const cur = CURATED_AREAS[id] || {};
    areas.push({
      id, kind: 'area', title: node.name, rootIds: [id],
      blurb: cur.blurb || '', accent: cur.accent || '#818cf8',
      count: rollupCount(node),
    });
  });
  const roles = ROLE_PATHS
    .map((r) => ({ ...r, rootIds: r.rootIds.filter((id) => byId[id]),
                   count: r.rootIds.reduce((n, id) => n + rollupCount(byId[id]), 0) }))
    .filter((r) => r.rootIds.length > 0);
  return { areas, roles, byId };
}

export function findPath(hubTree, pathId) {
  const { areas, roles } = buildPaths(hubTree);
  return [...areas, ...roles].find((p) => p.id === pathId) || null;
}

/** Attempt-weighted mastery over the path's roots in the MEMORY tree. */
export function pathMastery(path, memTree) {
  const byId = indexTree(memTree);
  let att = 0; let acc = 0;
  (path.rootIds || []).forEach((id) => {
    const n = byId[id];
    if (n && n.attempts > 0) { att += n.attempts; acc += (n.mastery || 0) * n.attempts; }
  });
  return att > 0 ? acc / att : null;
}

// Shared, briefly-cached hub tree so Home + PathDetail don't double-fetch.
let _hubPromise = null;
let _hubAt = 0;
export function getHubTreeCached() {
  const now = Date.now();
  if (!_hubPromise || now - _hubAt > 60000) {
    _hubAt = now;
    _hubPromise = APIService.getHubTree().catch((e) => { _hubPromise = null; throw e; });
  }
  return _hubPromise;
}
